<!-- src/views/Drone/pages/IndexPage.vue -->
<template>
  <div class="index-page">
    <!-- 页面头部 -->
    <div class="page-header">
      <h1 class="page-title">全国青少年国防素养大赛智能无人系统应用赛线上答题系统</h1>
      <p class="page-subtitle">专业、全面、智能的无人机学习与竞赛平台</p>
    </div>

    <!-- 主要内容区 -->
    <div class="main-content">
      <!-- 学习排名 -->
      <div class="rank-section">
        <div class="rank-container">
          <!-- 左侧：排名列表 -->
          <div class="rank-left">
            <!-- 左侧标题 -->
            <div class="rank-header">
              <i class="fas fa-trophy"></i>
              <h3>全国排名</h3>
            </div>
            <!-- 排名分组切换 -->
            <div class="rank-tabs">
              <button
                v-for="group in rankGroups"
                :key="group.value"
                :class="['rank-tab', { active: activeRankGroup === group.value }]"
                @click="activeRankGroup = group.value"
              >
                {{ group.label }}
              </button>
            </div>

            <!-- 个人排名卡片 -->
            <div class="personal-rank-card">
              <div class="personal-rank-icon">
                <i class="fas fa-user-circle"></i>
              </div>
              <div class="personal-rank-info">
                <div class="personal-rank-title">我的排名</div>
                <div class="personal-rank-desc">{{ personalRankDisplay.gradeLabel }}</div>
              </div>
              <div class="personal-rank-data">
                <div class="personal-rank-item">
                  <span class="personal-rank-value">{{ personalRankDisplay.rank || '-' }}</span>
                  <span class="personal-rank-label">当前排名</span>
                </div>
                <div class="personal-rank-item">
                  <span class="personal-rank-value">{{ personalRankDisplay.score || 0 }}</span>
                  <span class="personal-rank-label">我的分数</span>
                </div>
              </div>
            </div>

            <!-- 排名列表 -->
            <div class="rank-list">
              <div class="rank-list-header">
                <span>排名</span>
                <span>选手信息</span>
                <span>分数</span>
              </div>
              <div v-if="filteredRankList.length > 0" class="rank-item" v-for="(item, index) in filteredRankList" :key="index">
                <div class="rank-item-number">
                  <span class="rank-badge" :class="getRankClass(item.rank)">
                    {{ item.rank }}
                  </span>
                </div>
                <div class="rank-item-info">
                  <div class="rank-item-avatar">
                    <img v-if="item.avatar && item.avatar.trim()" :src="item.avatar" :alt="item.name" class="avatar-img" @error="handleAvatarError($event, item)">
                    <img v-else :src="'https://api.dicebear.com/7.x/avataaars/svg?seed=' + item.name" :alt="item.name" class="avatar-img">
                  </div>
                  <div class="rank-item-details">
                    <div class="rank-item-name">{{ item.name }}</div>
                    <div class="rank-item-school">
                      <i class="fas fa-school"></i> {{ item.school }}
                    </div>
                  </div>
                </div>
                <div class="rank-item-score">
                  <span class="score-value">{{ item.score }}</span>
                  <span class="score-unit">分</span>
                </div>
              </div>
              <div v-else class="rank-item empty">
                <div class="empty-icon">
                  <i class="fas fa-trophy"></i>
                </div>
                <div class="empty-text">暂无排名数据</div>
                <div class="empty-subtext">完成考试后即可参与排名</div>
              </div>
            </div>
          </div>

          <!-- 右侧：赛事资讯 -->
          <div class="news-right">
            <div class="news-header">
            <i class="fas fa-file-alt"></i>
            <h3>赛事文档</h3>
            <div class="news-count">{{ documentTotal }}</div>
          </div>
            <div class="news-list">
              <div class="news-item" v-for="(doc, index) in documents" :key="index" @click="openDocument(doc)">
                <div class="news-content">
                  <div class="news-title">{{ doc.title }}</div>
                  <div class="news-meta">
                    <span class="news-date">{{ formatDate(doc.createTime) }}</span>
                  </div>
                </div>
                <div class="news-arrow">
                  <i class="fas fa-file-download"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>



      <!-- 学习内容 -->
      <div class="content-section">
        <h2 class="section-title">学习资料</h2>
        <div class="book-grid">
          <div class="book-card" v-for="(book, index) in books" :key="index" @click="openEbook(book)">
            <div class="book-cover-wrapper">
              <div class="book-cover-3d">
                <div class="book-spine-effect"></div>
                <div class="book-cover">
                  <img :src="book.cover" :alt="book.name">
                  <div class="book-shine"></div>
                </div>
              </div>
              <div class="book-hover-overlay">
                <span class="read-btn"><i class="fas fa-book-open"></i> 点击阅读</span>
              </div>
            </div>
            <div class="book-info">
              <h3 class="book-name">{{ book.name }}</h3>
              <p class="book-desc">{{ book.description }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>


  </div>
</template>

<script>
import { getHomeKnowledgebase, getKnowledgebaseDetail, getLeaderboard, getNewsList, getWrjNewsList, getWrjNewsDetail } from '@/api/droneApi';
import { baseURL } from '@/api/axiosConfig';

export default {
  name: "IndexPage",
  data() {
    return {
      topRankers: [],
      rankList: [],
      
      // 排名分组配置
      rankGroups: [
        { label: '小学', value: '小学组' },
        { label: '初中', value: '初中组' },
        { label: '高中', value: '高中组' }
      ],
      activeRankGroup: '小学组',

      // 各组排行榜数据
      leaderboards: {
        '小学组': [],
        '初中组': [],
        '高中组': []
      },
      
      // 当前用户排名
      currentUserRank: {
        '小学组': { rank: '--', totalScore: '--' },
        '初中组': { rank: '--', totalScore: '--' },
        '高中组': { rank: '--', totalScore: '--' }
      },

      books: [],
      
      // 赛事资讯列表
      newsList: [],
      
      // 赛事文档列表
      documents: [],
      // 文档总数（用于显示）
      documentTotal: 0,
    };
  },
  computed: {
    // 根据选择的分组获取排行榜数据
    filteredRankList() {
      return this.leaderboards[this.activeRankGroup] || [];
    },
    // 根据当前分组获取个人排名显示信息
    personalRankDisplay() {
      const currentGroup = this.rankGroups.find(g => g.value === this.activeRankGroup);
      const userRank = this.currentUserRank[this.activeRankGroup] || { rank: '--', totalScore: '--' };
      return {
        rank: userRank.rank,
        score: userRank.totalScore,
        gradeLabel: currentGroup ? currentGroup.label + '组' : '未知'
      };
    }
  },
  created() {
    this.fetchHomeKnowledgebase();
    this.fetchLeaderboard();
    this.fetchNewsList();
    this.fetchDocuments();
  },
  methods: {
    // 获取首页学习资料
    async fetchHomeKnowledgebase() {
      try {
        const res = await getHomeKnowledgebase()
        if (res.data.code === 200 && res.data.data) {
          // 处理返回的数据，转换为页面需要的格式
          this.books = res.data.data.map(item => ({
            id: item.id,
            name: item.name,
            description: item.brief,
            cover: baseURL + item.imgurl,
            isPdf: true,
            pdfUrl: baseURL + item.pdfurl
          }))
        }
      } catch (error) {
        console.error('获取首页学习资料失败:', error)
      }
    },
    // 获取用户成绩排行榜
    async fetchLeaderboard() {
      try {
        console.log('开始获取排行榜数据...');
        const res = await getLeaderboard();
        console.log('接口返回数据:', res);
        
        if (res.data.code === 200 && res.data.data) {
          const data = res.data.data;
          console.log('排行榜数据:', data);
          
          // 处理各组排行榜数据
          if (data.leaderboards) {
            this.leaderboards = {
              '小学组': (data.leaderboards['小学组'] || []).map(item => ({
                rank: item.rank,
                name: item.phonenumber || '未知用户',
                school: item.paperName || '未知学校',
                score: item.totalScore || 0,
                avatar: item.avatar ? baseURL + item.avatar : ''
              })),
              '初中组': (data.leaderboards['初中组'] || []).map(item => ({
                rank: item.rank,
                name: item.phonenumber || '未知用户',
                school: item.paperName || '未知学校',
                score: item.totalScore || 0,
                avatar: item.avatar ? baseURL + item.avatar : ''
              })),
              '高中组': (data.leaderboards['高中组'] || []).map(item => ({
                rank: item.rank,
                name: item.phonenumber || '未知用户',
                school: item.paperName || '未知学校',
                score: item.totalScore || 0,
                avatar: item.avatar ? baseURL + item.avatar : ''
              }))
            };
          }
          
          // 处理当前用户排名
          if (data.currentUserRank) {
            this.currentUserRank = data.currentUserRank;
          }
          
          console.log('处理后的leaderboards:', this.leaderboards);
          console.log('处理后的currentUserRank:', this.currentUserRank);
        } else {
          console.error('接口返回错误:', res.data);
        }
      } catch (error) {
        console.error('获取排行榜数据失败:', error);
      }
    },
    
    // 根据排名获取样式类
    getRankClass(rank) {
      if (rank === 1) return 'gold';
      if (rank === 2) return 'silver';
      if (rank === 3) return 'bronze';
      return '';
    },
    
    // 头像加载失败时显示默认头像
    handleAvatarError(event, item) {
      console.error('头像加载失败:', item.avatar);
      event.target.src = 'https://api.dicebear.com/7.x/avataaars/svg?seed=' + item.name;
    },
    
    // 根据年级代码获取年级名称
    getGradeLabel(grade) {
      const gradeMap = {
        '1': '小学',
        '2': '初中',
        '3': '高中'
      };
      return gradeMap[grade] || '未知';
    },
    
    // 获取赛事资讯列表
    async fetchNewsList() {
      try {
        const res = await getNewsList();
        if (res.data.code === 200 && res.data.data) {
          this.newsList = res.data.data.map(news => ({
            id: news.id,
            title: news.title,
            content: news.content,
            date: news.date,
            hot: news.hot === 1
          }));
        }
      } catch (error) {
        console.error('获取赛事资讯失败:', error);
      }
    },
    
    // 获取赛事文档列表
    async fetchDocuments() {
      try {
        const res = await getWrjNewsList({ pageNum: 1, pageSize: 10 });
        if (res.data.code === 200 && res.data.data) {
          const data = res.data.data;
          this.documents = data.list || [];
          this.documentTotal = data.total || 0;
          console.log('赛事文档列表:', this.documents);
        }
      } catch (error) {
        console.error('获取赛事文档失败:', error);
      }
    },
    
    // 格式化日期
    formatDate(dateStr) {
      if (!dateStr) return '';
      const date = new Date(dateStr);
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit'
      });
    },
    
    // 打开资讯详情
    openNews(news) {
      if (news.content) {
        alert(news.content);
      }
    },
    
    // 打开文档
    async openDocument(doc) {
      try {
        console.log('获取文档详情, ID:', doc.id);
        const res = await getWrjNewsDetail(doc.id);
        if (res.data.code === 200 && res.data.data) {
          const docDetail = res.data.data;
          let docUrl = docDetail.pdfUrl;
          if (!docUrl) {
            console.error('文档URL为空');
            return;
          }
          // 如果是相对路径，添加baseURL
          if (!docUrl.startsWith('http')) {
            docUrl = baseURL + docUrl;
          }
          console.log('打开文档:', docUrl);
          // 在新标签页中打开文档
          window.open(docUrl, '_blank');
        } else {
          console.error('获取文档详情失败:', res.data.msg);
        }
      } catch (error) {
        console.error('获取文档详情失败:', error);
      }
    },
    
    // 打开电子书
    async openEbook(book) {
      try {
        // 从接口获取详细信息
        const res = await getKnowledgebaseDetail(book.id)
        
        if (res.data.code === 200 && res.data.data) {
          const detailData = res.data.data
          
          // 构建完整的书籍对象
          const bookData = {
            id: detailData.id,
            name: detailData.name,
            description: detailData.brief,
            cover: baseURL + detailData.imgurl,
            isPdf: true,
            pdfUrl: baseURL + detailData.pdfurl
          }
          
          // 在新窗口中打开 PDF
          window.open(bookData.pdfUrl, '_blank')
        } else {
          alert('获取书籍详情失败')
        }
      } catch (error) {
        console.error('获取书籍详情失败:', error)
        alert('获取书籍详情失败，请稍后重试')
      }
    },

  }
};
</script>

<style scoped>
/* 全局样式 */
.index-page {
  background: #f5f7fa;
  min-height: 100vh;
  padding-top: 5px;
}

/* 页面头部 */
.page-header {
  text-align: center;
  margin-bottom: 10px;
  padding: 1px 0;
  background: linear-gradient(135deg, #1a5f7a 0%, #159895 100%);
  color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(22, 160, 133, 0.3);
}

.page-title {
  font-size: 32px;
  font-weight: 600;
  margin-bottom: 10px;
}

.page-subtitle {
  font-size: 16px;
  opacity: 0.9;
  margin-bottom: 20px;
}



/* 主要内容区 */
.main-content {
  max-width: 1200px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: 40px;
}

/* 通用标题样式 */
.section-title {
  font-size: 24px;
  font-weight: 600;
  color: #333;
  margin-bottom: 25px;
  padding-bottom: 10px;
  border-bottom: 3px solid #667eea;
}



/* 排名区域 */
.rank-section {
  background: linear-gradient(135deg, #1a5f7a 0%, #159895 100%);
  border-radius: 16px;
  padding: 15px 20px 35px;
  box-shadow: 0 10px 30px rgba(22, 160, 133, 0.3);
}

/* 左侧排名标题 */
.rank-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 8px;
  border-bottom: 2px solid rgba(102, 126, 234, 0.2);
  margin-bottom: 8px;
}

.rank-header i {
  font-size: 16px;
  color: #667eea;
}

.rank-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.rank-container {
  display: flex;
  gap: 20px;
  align-items: stretch;
}

/* 左侧排名列表 */
.rank-left {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 8px;
  min-width: 0;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 12px;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.05);
  max-height: 450px;
  overflow-y: auto;
}

/* 右侧赛事资讯 */
.news-right {
  flex: 1;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 12px;
  padding: 12px;
  box-shadow: inset 0 2px 10px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  max-height: 450px;
  overflow-y: auto;
  margin-top: 15px;
}

.news-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding-bottom: 10px;
  border-bottom: 2px solid rgba(102, 126, 234, 0.2);
  margin-bottom: 10px;
}

.news-header i {
  font-size: 16px;
  color: #667eea;
}

.news-header h3 {
  margin: 0;
  font-size: 15px;
  font-weight: 600;
  color: #333;
}

.news-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-bottom: 20px;
  min-height: 0;
}

.news-item {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 8px 10px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: all 0.3s ease;
}

.news-item:hover {
  background: rgba(255, 255, 255, 1);
  border-color: rgba(102, 126, 234, 0.2);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.15);
  transform: translateX(5px);
}

.news-content {
  flex: 1;
  min-width: 0;
}

.news-title {
  font-size: 13px;
  font-weight: 500;
  color: #333;
  margin-bottom: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.news-meta {
  display: flex;
  align-items: center;
  gap: 10px;
}

.news-date {
  font-size: 11px;
  color: #999;
}

.news-hot {
  font-size: 10px;
  color: #ff6b6b;
  display: flex;
  align-items: center;
  gap: 3px;
}

.news-hot i {
  font-size: 10px;
}

.news-arrow {
  color: #ccc;
  font-size: 12px;
  transition: all 0.3s ease;
}

.news-item:hover .news-arrow {
  color: #667eea;
  transform: translateX(3px);
}

.news-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex: 1;
  color: #999;
  font-size: 13px;
  text-align: center;
  padding: 30px 0;
}

/* 空状态样式 */
.empty-icon {
  font-size: 36px;
  color: rgba(102, 126, 234, 0.2);
  margin-bottom: 10px;
  animation: pulse 2s infinite;
}

@keyframes pulse {
  0%, 100% { transform: scale(1); opacity: 0.2; }
  50% { transform: scale(1.1); opacity: 0.3; }
}

.empty-subtext {
  color: #ccc;
  font-size: 12px;
  text-align: center;
  line-height: 1.4;
}

/* 头像图片样式 */
.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.3s ease;
}

.rank-item:hover .avatar-img {
  transform: scale(1.05);
}

/* 资讯数量 */
.news-count {
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  font-size: 12px;
  padding: 2px 8px;
  border-radius: 10px;
  font-weight: 600;
  margin-left: auto;
}

/* 个人排名卡片 */
.personal-rank-card {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 12px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-radius: 8px;
  margin-bottom: 6px;
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.3);
}

.personal-rank-icon {
  font-size: 24px;
  color: #fff;
  opacity: 0.9;
}

.personal-rank-info {
  flex: 1;
}

.personal-rank-title {
  font-size: 13px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 2px;
}

.personal-rank-desc {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.8);
}

.personal-rank-data {
  display: flex;
  gap: 15px;
}

.personal-rank-item {
  text-align: center;
}

.personal-rank-value {
  display: block;
  font-size: 18px;
  font-weight: bold;
  color: #fff;
}

.personal-rank-label {
  font-size: 10px;
  color: rgba(255, 255, 255, 0.7);
}

/* 排名分组切换 */
.rank-tabs {
  display: flex;
  gap: 6px;
  margin-bottom: 6px;
  padding-bottom: 6px;
  border-bottom: 2px solid rgba(102, 126, 234, 0.2);
  justify-content: center;
}

.rank-tab {
  padding: 5px 12px;
  border: 2px solid rgba(102, 126, 234, 0.3);
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border-radius: 16px;
  cursor: pointer;
  transition: all 0.3s ease;
  font-weight: 500;
  font-size: 12px;
}

.rank-tab:hover {
  background: rgba(102, 126, 234, 0.2);
  border-color: rgba(102, 126, 234, 0.5);
  transform: translateY(-2px);
}

.rank-tab.active {
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  border-color: #667eea;
  color: #fff;
  box-shadow: 0 4px 12px rgba(102, 126, 234, 0.4);
}

/* 排名列表 */
.rank-list {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 4px;
  
}

.rank-list::-webkit-scrollbar {
  width: 4px;
}

.rank-list::-webkit-scrollbar-track {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 2px;
}

.rank-list::-webkit-scrollbar-thumb {
  background: rgba(102, 126, 234, 0.3);
  border-radius: 2px;
}

.rank-list::-webkit-scrollbar-thumb:hover {
  background: rgba(102, 126, 234, 0.5);
}

.rank-list-header {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  background: linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%);
  border-radius: 6px;
  font-weight: 600;
  color: #667eea;
  font-size: 11px;

  margin-bottom: 4px;
}

.rank-list-header span:first-child {
  width: 45px;
  text-align: center;
}

.rank-list-header span:nth-child(2) {
  flex: 1;
  margin-left: 22px;
}

.rank-list-header span:last-child {
  width: 55px;
  text-align: right;
}

.rank-item {
  display: flex;
  align-items: center;
  padding: 5px 8px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 6px;
  transition: all 0.3s ease;
  border: 1px solid transparent;
  animation: fadeInUp 0.4s ease forwards;
  opacity: 0;
  transform: translateY(10px);
}

.rank-item:nth-child(1) { animation-delay: 0.05s; }
.rank-item:nth-child(2) { animation-delay: 0.1s; }
.rank-item:nth-child(3) { animation-delay: 0.15s; }
.rank-item:nth-child(4) { animation-delay: 0.2s; }
.rank-item:nth-child(5) { animation-delay: 0.25s; }
.rank-item:nth-child(6) { animation-delay: 0.3s; }
.rank-item:nth-child(7) { animation-delay: 0.35s; }
.rank-item:nth-child(8) { animation-delay: 0.4s; }
.rank-item:nth-child(9) { animation-delay: 0.45s; }
.rank-item:nth-child(10) { animation-delay: 0.5s; }

@keyframes fadeInUp {
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.rank-item:hover {
  background: rgba(255, 255, 255, 1);
  border-color: rgba(102, 126, 234, 0.2);
  box-shadow: 0 4px 15px rgba(102, 126, 234, 0.15);
  transform: translateX(5px);
}

.rank-item.empty {
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;
  background: rgba(255, 255, 255, 0.5);
  animation: none;
  opacity: 1;
  transform: none;
}

.empty-text {
  color: #999;
  font-size: 13px;
  text-align: center;
  margin-bottom: 6px;
  font-weight: 500;
}

.empty-subtext {
  color: #ccc;
  font-size: 11px;
  text-align: center;
  line-height: 1.4;
}

.rank-item-number {
  width: 50px;
  text-align: center;
}

.rank-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  color: #fff;
  border-radius: 50%;
  font-weight: bold;
  font-size: 11px;
  box-shadow: 0 2px 6px rgba(102, 126, 234, 0.3);
}

.rank-badge.gold {
  background: linear-gradient(135deg, #ffd700, #ffed4e);
  color: #333;
  box-shadow: 0 2px 8px rgba(255, 215, 0, 0.5);
}

.rank-badge.silver {
  background: linear-gradient(135deg, #c0c0c0, #e8e8e8);
  color: #333;
  box-shadow: 0 2px 8px rgba(192, 192, 192, 0.5);
}

.rank-badge.bronze {
  background: linear-gradient(135deg, #cd7f32, #e8c8a8);
  color: #333;
  box-shadow: 0 2px 8px rgba(205, 127, 50, 0.5);
}

.rank-item-info {
  flex: 1;
  display: flex;
  align-items: center;
  margin-left: 8px;
}

.rank-item-avatar {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  overflow: hidden;
  border: 2px solid rgba(102, 126, 234, 0.2);
  margin-right: 8px;
  background: #fff;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.1);
}

.rank-item-avatar img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.rank-item-details {
  flex: 1;
}

.rank-item-name {
  font-size: 13px;
  font-weight: 600;
  color: #333;
  margin-bottom: 2px;
}

.rank-item-school {
  font-size: 11px;
  color: #888;
  margin-bottom: 2px;
}

.rank-item-grade {
  font-size: 10px;
  color: #999;
}

.rank-item-score {
  width: 60px;
  text-align: right;
  display: flex;
  align-items: baseline;
  justify-content: flex-end;
}

.score-value {
  font-size: 14px;
  font-weight: bold;
  color: #667eea;
  margin-right: 2px;
}

.score-unit {
  font-size: 12px;
  color: #888;
}



/* 学习内容区域 */
.content-section {
  background: #fff;
  border-radius: 12px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.08);
}

.book-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 30px;
}

.book-card {
  background: linear-gradient(145deg, #ffffff 0%, #f8f9fa 100%);
  border-radius: 16px;
  padding: 25px;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  border: 1px solid rgba(0, 0, 0, 0.06);
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  overflow: hidden;
}

.book-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 4px;
  background: linear-gradient(90deg, #1a5f7a, #159895, #57c5b6);
  opacity: 0;
  transition: opacity 0.3s;
}

.book-card:hover {
  transform: translateY(-8px);
  box-shadow: 0 20px 40px rgba(21, 152, 149, 0.15),
              0 8px 16px rgba(0, 0, 0, 0.08);
  border-color: rgba(21, 152, 149, 0.2);
}

.book-card:hover::before {
  opacity: 1;
}

.book-cover-wrapper {
  position: relative;
  margin-bottom: 20px;
}

.book-cover-3d {
  position: relative;
  perspective: 1000px;
}

.book-spine-effect {
  position: absolute;
  left: -8px;
  top: 5%;
  width: 12px;
  height: 90%;
  background: linear-gradient(to right, #d4a574, #c4956a, #d4a574);
  border-radius: 2px 0 0 2px;
  transform: rotateY(-90deg);
  transform-origin: right center;
  box-shadow: inset -2px 0 4px rgba(0, 0, 0, 0.2);
}

.book-cover {
  width: 140px;
  height: 190px;
  border-radius: 8px;
  overflow: hidden;
  position: relative;
  box-shadow: 
    -4px 4px 10px rgba(0, 0, 0, 0.2),
    4px 4px 10px rgba(0, 0, 0, 0.1),
    0 10px 30px rgba(0, 0, 0, 0.15);
  transform: perspective(800px) rotateY(-5deg);
  transition: all 0.4s ease;
}

.book-card:hover .book-cover {
  transform: perspective(800px) rotateY(0deg);
  box-shadow: 
    0 15px 40px rgba(0, 0, 0, 0.2),
    0 8px 20px rgba(0, 0, 0, 0.15);
}

.book-cover img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.book-card:hover .book-cover img {
  transform: scale(1.05);
}

.book-shine {
  position: absolute;
  top: 0;
  left: -100%;
  width: 50%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(255, 255, 255, 0) 0%,
    rgba(255, 255, 255, 0.2) 50%,
    rgba(255, 255, 255, 0) 100%
  );
  transition: left 0.6s ease;
}

.book-card:hover .book-shine {
  left: 150%;
}

.book-pages-count {
  position: absolute;
  bottom: -10px;
  right: -10px;
  background: linear-gradient(135deg, #1a5f7a, #159895);
  color: white;
  font-size: 11px;
  padding: 4px 10px;
  border-radius: 20px;
  box-shadow: 0 3px 10px rgba(21, 152, 149, 0.4);
  font-weight: 500;
}

.book-badge {
  position: absolute;
  top: -5px;
  left: -5px;
  background: linear-gradient(135deg, #ff6b6b, #ee5a24);
  color: white;
  font-size: 11px;
  padding: 4px 12px;
  border-radius: 20px;
  box-shadow: 0 3px 10px rgba(238, 90, 36, 0.4);
  font-weight: 600;
  z-index: 2;
}

.book-hover-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(21, 152, 149, 0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: 0;
  transition: opacity 0.3s ease;
  border-radius: 8px;
  z-index: 5;
}

.book-cover-wrapper:hover .book-hover-overlay {
  opacity: 1;
}

.read-btn {
  color: white;
  font-size: 14px;
  font-weight: 600;
  display: flex;
  align-items: center;
  gap: 8px;
}

.read-btn i {
  font-size: 18px;
}

.book-info {
  width: 100%;
  text-align: center;
}

.book-name {
  font-size: 17px;
  font-weight: 700;
  color: #2c3e50;
  margin-bottom: 10px;
  line-height: 1.3;
  transition: color 0.3s;
}

.book-card:hover .book-name {
  color: #159895;
}

.book-desc {
  font-size: 13px;
  color: #7f8c8d;
  line-height: 1.6;
  margin-bottom: 15px;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}



/* 响应式设计 */
@media (max-width: 1200px) {
  .book-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 992px) {
  .rank-container {
    flex-direction: column;
  }
  
  .rank-left,
  .news-right {
    max-height: 350px;
  }
}

@media (max-width: 768px) {
  .page-title {
    font-size: 20px;
  }
  
  .rank-container {
    flex-direction: column;
    gap: 15px;
  }
  
  .rank-left,
  .news-right {
    min-width: 100%;
    max-height: 320px;
  }
  
  .main-content {
    padding: 0 8px;
  }
  
  .rank-section,
  .content-section {
    padding: 15px;
  }
  
  .rank-tabs {
    flex-wrap: wrap;
    justify-content: center;
    gap: 6px;
  }
  
  .rank-tab {
    padding: 5px 12px;
    font-size: 12px;
  }
}

/* 防止在中等屏幕尺寸下重叠 */
@media (max-width: 1024px) {
  .rank-container {
    flex-direction: column;
  }
  
  .rank-left,
  .news-right {
    width: 100%;
    max-height: 400px;
  }
}


</style>